/**
 * Liked Songs Page
 *
 * Shows all liked songs with play/shuffle options.
 */

import { useLikesStore } from '@/stores/likes';
import { usePlayerStore } from '@/stores/player';
import { PlayIcon, ShuffleIcon, HeartIcon } from '@/components/icons';
import { CachedImage } from '@/components/common/CachedImage';

export function LikedSongsPage() {
  const { likedSongs, unlike } = useLikesStore();
  const { play } = usePlayerStore();

  const songs = likedSongs.map((s) => ({
    id: s.id,
    title: s.title,
    artists: s.artists,
    thumbnail: s.thumbnail,
    thumbnails: s.thumbnails,
  }));

  const handlePlayAll = () => {
    if (songs.length > 0) {
      play(songs[0] as any, songs as any[]);
    }
  };

  const handleShuffle = () => {
    if (songs.length > 0) {
      const shuffled = [...songs].sort(() => Math.random() - 0.5);
      play(shuffled[0] as any, shuffled as any[]);
    }
  };

  return (
    <div className="min-h-full">
      {/* Header */}
      <header className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/30 to-black" />
        <div className="relative px-4 pt-8 pb-6 flex flex-col sm:flex-row items-center gap-6">
          {/* Liked icon */}
          <div className="w-40 h-40 rounded-xl bg-gradient-to-br from-primary to-primary-700 flex items-center justify-center shadow-2xl">
            <HeartIcon className="w-20 h-20 text-white" />
          </div>

          {/* Info */}
          <div className="text-center sm:text-left flex-1">
            <p className="text-sm text-on-surface-variant uppercase tracking-wider mb-1">
              Playlist
            </p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
              Liked Songs
            </h1>
            <p className="text-on-surface-variant mb-4">
              {likedSongs.length} {likedSongs.length === 1 ? 'song' : 'songs'}
            </p>

            {/* Action buttons */}
            {likedSongs.length > 0 && (
              <div className="flex items-center justify-center sm:justify-start gap-3">
                <button className="btn-primary flex items-center gap-2" onClick={handlePlayAll}>
                  <PlayIcon className="w-5 h-5" />
                  Play
                </button>
                <button className="btn-secondary flex items-center gap-2" onClick={handleShuffle}>
                  <ShuffleIcon className="w-5 h-5" />
                  Shuffle
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Track list */}
      <div className="px-4 pb-8">
        {likedSongs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-on-surface-variant">No liked songs yet</p>
            <p className="text-sm text-on-surface-variant/60 mt-1">
              Tap the heart icon on any song to add it here
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {likedSongs.map((song, index) => {
              const thumbnail = song.thumbnail || song.thumbnails?.[0]?.url || './pwa-192x192.webp';
              return (
                <div
                  key={song.id}
                  className="flex items-center gap-3 py-2 px-1 hover:bg-surface-container/50 transition-colors rounded-lg group"
                >
                  <span className="w-6 text-center text-on-surface-variant text-sm shrink-0">
                    {index + 1}
                  </span>
                  <button
                    onClick={() => play(songs[index] as any, songs as any[])}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    <CachedImage
                      src={thumbnail}
                      alt={song.title}
                      className="w-12 h-12 rounded-lg object-cover shrink-0"
                    />
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-medium line-clamp-1">{song.title}</p>
                      <p className="text-sm text-on-surface-variant line-clamp-1">
                        {song.artists?.map((a) => a.name).join(', ')}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={() => unlike(song.id)}
                    className="btn-icon text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    aria-label="Unlike"
                  >
                    <HeartIcon className="w-5 h-5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
